import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';

import { Route, Switch } from 'react-router-dom';
import Loading from '../../../cisview/oldWebShared/aip/loading';
import { rootPath as corrMonitorPath, fallbackPath as assetViewPath } from '../../../assetView/redux/routing/util/routepath';
import { identify } from '../../../util/analytics/segment';
import {
	getCustomer,
	getUserName,
	getUserUUID,
	getEmail
} from '../../../util/user';
import { mustSelectCompany } from '../../../util/user/companySwitch';
import SwitchCompanyPage from '../CompanySwitch/SwitchCompanyPage';
import CachedRouteSwitch from './CachedRouteSwitch';

import {
	AppAccount,
	AppScanline,
	AppCISView,
	AppFieldLine,
	DataIntegrity,
	AppChargeLine,
	AppAssetView,
	AppLiveLine,
	AppVideoLine,
	AppTemplateBuilder,
	AppSettings,
	DashboardsAndReports,
	WebsiteBridge,
	Applications,
	ApiDocs,
	AllProjectsPage,
	MyProjectsPage,
	CorrMonitorPage,
	CorrMonitorProjectPage,
	AssetDetailPage,
	CorrLoaderPage,
	CorrLoaderJobPage,
	MyAccountPage,
	HelpSupportPage,
	SpectrumXLIPage,
	SpectrumXliDetailPage,
	TrackingPage,
	TrackingRunPage,
	AssetPhotoUploadPage
} from './LazyLoading';
import LeftPanel from '../LeftPanel';
import AccessDenied from '../shared/AccessDenied';

import {
	getCompanyProducts as acGetCompanyProducts
} from '../../redux/actions';
import { selectCompanyProducts } from '../../redux/selector';

const MainRouter = () => {
	const dispatch = useDispatch();
	// Product-data flags for the company being viewed (null while unresolved).
	const companyProducts = useSelector(selectCompanyProducts);
	// The CorrMonitor guards depend on the product flags, so the router must not
	// conclude a URL is unroutable before they have resolved — an unresolved
	// answer used to fall through to the empty application shell (blank page).
	const [accessResolved, setAccessResolved] = useState(
		() => companyProducts !== null
	);

	useEffect(() => {
		if (accessResolved) { return undefined; }
		let cancelled = false;
		const settle = () => {
			if (!cancelled) { setAccessResolved(true); }
		};
		// The lookup resolves rather than rejects, so the router always settles.
		Promise.resolve(dispatch(acGetCompanyProducts())).then(settle, settle);
		return () => {
			cancelled = true;
		};
	}, [accessResolved, dispatch]);

	// Shared with the sidebar (see LeftPanel): the viewed company has data for it.
	const hasCorrMonitor = useMemo(
		() => Boolean(companyProducts && (companyProducts.hasCp || companyProducts.hasXli)),
		[companyProducts]
	);

	identify(getUserUUID(), {
		customer: getCustomer(),
		username: getUserName(),
		email: getEmail()
	});

	const pageRoutes = useMemo(() => [
		{ path: '/Account', component: AppAccount },

		// CORR LOADER — new pages (jobs list + read-only job overview)
		{
			exact: true,
			path: '/CorrLoader',
			component: CorrLoaderPage
		},
		{
			path: '/CorrLoader/overview/:job',
			component: CorrLoaderJobPage,
			getCacheKey: location => `/CorrLoader/overview${location.pathname}`
		},

		// CORR LOADER — legacy job management (list/create/edit)
		{
			path: '/CorrLoader/manage',
			component: AppCISView
		},

		// CORR LOADER — legacy editor (/CorrLoader/Job/...)
		{
			path: '/CorrLoader',
			component: AppCISView
		},
		{
			path: '/CISLine',
			component: AppCISView
		},

		// SPECTRUM XLI — in-line inspection surveys (cards + map).
		{
			exact: true,
			path: '/SpectrumXLI',
			component: SpectrumXLIPage
		},

		// CORR LOGIX
		{
			path: '/CorrLogix',
			component: AppScanline
		},
		{
			path: '/Scanline',
			component: AppScanline
		},

		// CORR LOGIX DATA
		{
			path: '/CorrLogixData',
			component: AppFieldLine
		},
		{
			path: '/Fieldline',
			component: AppFieldLine
		},
		{
			path: '/FieldlineReact',
			component: AppFieldLine
		},

		// CORR MONITOR — retired CP workspace page.
		hasCorrMonitor && {
			path: '/CorrMonitor/CP/Workspace/:id',
			redirectTo: '/CorrMonitor',
			cache: false
		},

		// CORR MONITOR — CP project asset detail (nested).
		hasCorrMonitor && {
			path: '/CorrMonitor/CP/Project/:id/Asset/:assetId',
			component: AssetDetailPage,
			getCacheKey: location => location.pathname
		},

		// CORR MONITOR — single CP project details (scoped by project id).
		{
			path: '/CorrMonitor/CP/Project/:id',
			component: CorrMonitorProjectPage,
			getCacheKey: location => location.pathname
		},

		// SPECTRUM XLI — project details.
		{
			path: '/CorrMonitor/XLI/Project/:id',
			component: SpectrumXliDetailPage,
			getCacheKey: location => location.pathname
		},

		// CORR MONITOR — map + asset list (catch-all; keep last).
		hasCorrMonitor && {
			exact: true,
			path: '/CorrMonitor/:zoom?/:lat?/:lon?',
			component: CorrMonitorPage,
			getCacheKey: () => '/CorrMonitor'
		},

		// CORR MONITOR — legacy assetView (now /CorrMonitorOld).
		hasCorrMonitor && {
			path: corrMonitorPath,
			component: AppAssetView
		},
		hasCorrMonitor && {
			path: assetViewPath,
			component: AppAssetView
		},

		// TRACKING (Armadillo LiveMap pig tracking).
		{
			path: '/Tracking/run/:runId',
			component: TrackingRunPage,
			getCacheKey: location => location.pathname
		},
		{
			path: '/Tracking/:zoom?/:lat?/:lon?',
			component: TrackingPage,
			getCacheKey: () => '/Tracking'
		},

		// Terminal routes for the guarded namespaces above.
		{ path: '/CorrLoader', component: AccessDenied, cache: false },
		{ path: '/SpectrumXLI', component: AccessDenied, cache: false },
		{ path: '/CorrMonitor', component: AccessDenied, cache: false },

		{ path: '/dataintegrity', component: DataIntegrity },
		{
			path: '/ChargeLine',
			component: AppChargeLine
		},
		{
			path: '/LiveLine',
			component: AppLiveLine
		},
		{
			path: '/VideoLine',
			component: AppVideoLine
		},
		{ path: '/templateBuilder', component: AppTemplateBuilder },
		{ path: '/CustomerUsers', component: AppSettings },
		{ path: '/AppList', component: Applications },
		{
			path: '/dashboards-and-reports',
			component: DashboardsAndReports
		},

		// Feature pages
		{ path: '/switch-company', component: SwitchCompanyPage },
		{ path: '/all-projects', component: AllProjectsPage },
		{ path: '/my-projects', component: MyProjectsPage },
		{ path: '/my-account', component: MyAccountPage },
		{ path: '/help-support', component: HelpSupportPage },

		// ADMIN — bulk CP asset photo upload.
		{ path: '/admin/asset-photos', component: AssetPhotoUploadPage },

		{ path: '/api-docs', component: ApiDocs },
		{ path: '/Index', component: WebsiteBridge },
		{ exact: true, path: '/', redirectTo: '/Applications', cache: false },
		window.hideReactApplcations === undefined && {
			path: '/',
			component: Applications
		}
	].filter(Boolean), [hasCorrMonitor]);

	// Operators must pick a company before they can use the app. Until they do,
	// the whole app is replaced by the blocking company picker.
	if (mustSelectCompany()) {
		return <SwitchCompanyPage forced />;
	}

	// Rendered in both branches below: the shell must stay mounted while the
	// product flags are resolving.
	const shellRoutes = (
		<Switch>
			<Route path="/Index" component={null} />
			<Route exact path="/" component={null} />
			<Route path="" component={LeftPanel} />
		</Switch>
	);

	if (!accessResolved) {
		return (
			<>
				<Loading title="Loading..." />
				{shellRoutes}
			</>
		);
	}

	return (
		<>
			<Suspense fallback={<Loading title="Loading..." />}>
				<CachedRouteSwitch routes={pageRoutes} />
			</Suspense>
			{shellRoutes}
		</>
	);
};

export default connect()(MainRouter);
